import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { WORKFLOW_ROLE_TYPES } from '../../../workflow/actionRegistry';
import {
  fetchWorkflowActionDefinitions,
  fetchMilestoneTemplateActionsForMilestone,
  replaceMilestoneTemplateActions,
} from '../../../services/workflowAdminService';

const inputClass =
  'w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm';

const Modal = ({ isOpen, onClose, title, children, footer }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50 animate-fade-in"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-card border border-border shadow-xl w-full sm:max-w-2xl max-h-[min(92dvh,92vh)] min-h-0 overflow-hidden flex flex-col animate-slide-in rounded-t-2xl sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-border">
          <h2 className="text-base sm:text-lg font-semibold text-foreground min-w-0">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="modal-scroll flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const roleLabel = (r) =>
  ({
    administrator: 'Administrateur',
    dentist_client: 'Dentiste client',
    dentist_supplier: 'Prothésiste / fournisseur',
    operator_3d: 'Opérateur 3D',
    operator_thermo: 'Opérateur thermo',
    operator_finition: 'Opérateur finition',
    operator_qualite: 'Opérateur qualité',
    livraison: 'Livraison / expédition',
    production_responsible: 'Resp. production',
    logistics_coordinator: 'Coord. logistique',
    distributor: 'Distributeur',
    production_supplier: 'Fournisseur production',
  }[r] || r);

/**
 * Configure workflow actions + permissions for one milestone template.
 */
const MilestoneWorkflowModal = ({ isOpen, onClose, milestone, phaseLabel, onSaved, workflowRoleOptions }) => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** action_key -> { enabled: boolean, roles: Set<string> } */
  const [selection, setSelection] = useState({});

  useEffect(() => {
    if (!isOpen || !milestone?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [defs, existing] = await Promise.all([
          fetchWorkflowActionDefinitions(),
          fetchMilestoneTemplateActionsForMilestone(milestone.id),
        ]);
        if (cancelled) return;
        setDefinitions(defs);
        const next = {};
        for (const d of defs) {
          const row = existing.find((e) => e.action_key === d.action_key);
          next[d.action_key] = {
            enabled: !!row,
            roles: new Set(row?.allowed_role_types?.length ? row.allowed_role_types : ['administrator']),
          };
        }
        setSelection(next);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, milestone?.id]);

  const toggleAction = (actionKey) => {
    setSelection((prev) => ({
      ...prev,
      [actionKey]: {
        ...prev[actionKey],
        enabled: !prev[actionKey]?.enabled,
      },
    }));
  };

  const toggleRole = (actionKey, role) => {
    setSelection((prev) => {
      const cur = prev[actionKey] || { enabled: true, roles: new Set(['administrator']) };
      const roles = new Set(cur.roles);
      if (roles.has(role)) roles.delete(role);
      else roles.add(role);
      if (roles.size === 0) roles.add('administrator');
      return { ...prev, [actionKey]: { ...cur, roles } };
    });
  };

  const handleSave = async () => {
    if (!milestone?.id) return;
    setSaving(true);
    try {
      const rows = [];
      let order = 0;
      for (const d of definitions) {
        const sel = selection[d.action_key];
        if (!sel?.enabled) continue;
        rows.push({
          action_key: d.action_key,
          sort_order: order++,
          allowed_role_types: Array.from(sel.roles || ['administrator']),
          config: {},
        });
      }
      await replaceMilestoneTemplateActions(milestone.id, rows);
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Erreur à l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(() => {
    const mn = milestone?.milestone_name || 'Jalon';
    return `Workflow — ${mn}${phaseLabel ? ` (${phaseLabel})` : ''}`;
  }, [milestone, phaseLabel]);

  const rolesForCheckboxGrid = useMemo(() => {
    const base =
      workflowRoleOptions?.length > 0
        ? workflowRoleOptions
        : WORKFLOW_ROLE_TYPES.map((roleType) => ({
            roleType,
            roleName: roleLabel(roleType),
          }));
    const map = new Map(base.map((o) => [o.roleType, o]));
    for (const sel of Object.values(selection)) {
      for (const r of sel?.roles || []) {
        if (!map.has(r)) map.set(r, { roleType: r, roleName: roleLabel(r) });
      }
    }
    return [...map.values()].sort((a, b) =>
      String(a.roleName || roleLabel(a.roleType)).localeCompare(
        String(b.roleName || roleLabel(b.roleType)),
        'fr',
        { sensitivity: 'base' }
      )
    );
  }, [workflowRoleOptions, selection]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Cochez les actions disponibles pour ce jalon et les rôles autorisés. Les actions sont exécutées côté application
            (handlers enregistrés dans le code).
          </p>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {definitions.map((def) => {
              const sel = selection[def.action_key] || { enabled: false, roles: new Set() };
              return (
                <div key={def.action_key} className="border border-border rounded-lg p-4 bg-muted/20">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sel.enabled}
                      onChange={() => toggleAction(def.action_key)}
                      className="mt-1 rounded border-border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        {def.icon && <Icon name={def.icon} size={16} className="text-primary flex-shrink-0" />}
                        <span>{def.label}</span>
                        <code className="text-xs text-muted-foreground font-mono">{def.action_key}</code>
                      </div>
                      {def.description && <p className="text-xs text-muted-foreground mt-1">{def.description}</p>}
                      {sel.enabled && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {rolesForCheckboxGrid.map(({ roleType, roleName }) => {
                            const label =
                              typeof roleName === 'string' && roleName.trim() !== ''
                                ? roleName
                                : roleLabel(roleType);
                            return (
                              <label
                                key={roleType}
                                className="inline-flex items-center gap-1.5 text-xs bg-card border border-border rounded px-2 py-1 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={sel.roles?.has(roleType)}
                                  onChange={() => toggleRole(def.action_key, roleType)}
                                  className="rounded border-border"
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Annuler
            </Button>
            <Button variant="default" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default MilestoneWorkflowModal;
